import type { ThemeConfig } from 'antd'

import { scalePx } from '@/utils/hubScale'

/**
 * The Ant Design half of the project-hub enlargement (see `scale.ts`).
 *
 * The hub's own styled-components are scaled by authoring them in `px()`, but
 * every AntD widget rendered *inside* them — buttons, inputs, selects, date
 * pickers, modals, tables, tags, steps — is sized from AntD's own px seed
 * tokens. Without this theme they stay at the base scale, and the hub reads as
 * oversized cards wrapped around normal-size controls.
 *
 * AntD's default algorithm derives nearly the whole token set from four seeds,
 * so scaling those four scales everything downstream:
 *
 * - `fontSize`     → `fontSizeSM/LG/XL`, `fontSizeHeading1..5`, every line
 *                    height and `fontHeight*` (`genFontMapToken`).
 * - `controlHeight` → `controlHeightXS/SM/LG`, i.e. the height of every input,
 *                    button, select and picker (`genControlHeight`).
 * - `sizeUnit`     → the whole `size*` map, which the alias layer re-exports as
 *                    every `padding*` and `margin*` token (`genSizeMapToken`).
 * - `borderRadius` → `borderRadiusLG/SM/XS/Outer` (`genRadius`).
 *
 * Deliberately NOT scaled:
 *
 * - `sizeStep` — the size map is `sizeUnit * (sizeStep ± n)`. Scaling the unit
 *   multiplies the map by exactly `HUB_SCALE`; scaling the step too would
 *   compound it into a much larger, non-linear enlargement.
 * - `lineWidth` — hairline borders should stay 1px crisp at any scale.
 *
 * This config is *merged* into the app-wide theme (`utils/theme.ts`) by the
 * nested `ConfigProvider` in `HubScaleProvider`, so the app's colors and Layout
 * overrides carry through untouched; only the sizes below are replaced. The
 * `Table` block re-states the app theme's own px overrides at hub scale — a
 * merged component override is per-key, so values it pins would otherwise
 * survive at base size.
 */
export const hubTheme: ThemeConfig = {
  token: {
    fontSize: scalePx(14),
    controlHeight: scalePx(32),
    sizeUnit: scalePx(4),
    borderRadius: scalePx(6),
  },
  components: {
    Table: {
      cellFontSize: scalePx(12),
      cellFontSizeMD: scalePx(12),
      cellFontSizeSM: scalePx(12),
      cellPaddingBlock: scalePx(8),
      cellPaddingBlockMD: scalePx(8),
      cellPaddingBlockSM: scalePx(8),
    },
  },
}
